import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import HighchartsComponentAlt from 'components/HighchartsComponentAlt';
import { getJSONObject } from 'utils/helper';
// Removed chart list integration
import DOMPurify from 'dompurify';
import Layout from 'components/Layout';
import styles from 'styles/dashboardGraphPopup.module.css';
import { getDashboardChartInitial, getDashboardChartFiltered } from 'api/dashboard';

const DashboardDetailWindow = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const qDashboardId = search.get('dashboard_id');
    const qChartId = search.get('chart_id');

    const { dashboards } = useSelector(state => state.DashboardReducer);

    // Local date filters
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isApplying, setIsApplying] = useState(false);
    const [overrideChart, setOverrideChart] = useState(null);
    const [isFilterable, setIsFilterable] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [baseChart, setBaseChart] = useState(null);

    // No dependency on chart list; rely solely on query params APIs

    // Fetch initial detail + filterable flag from new API
    useEffect(() => {
        const loadInitialDetail = async () => {
            const chartIdParam = qChartId;
            const dashIdParam = qDashboardId;
            if (!chartIdParam || !dashIdParam) {
                navigate('/dashboard');
                return;
            }
            try {
                const resp = await getDashboardChartInitial({ chartid: chartIdParam, dashboard_id: dashIdParam });
                // Expected shape: [ [ { ...fields } ], true/false ]
                const rows = Array.isArray(resp?.[0]) ? resp[0] : [];
                const chartRow = rows?.[0] ?? null;
                const filterable = Boolean(resp?.[1]);
                setIsFilterable(filterable);
                if (chartRow) {
                    const normalized = {
                        chartid: chartIdParam,
                        dashboard_id: dashIdParam,
                        graph_type: chartRow.graph_type ?? null,
                        graph_head: chartRow.graph_head ?? null,
                        highchart_code: chartRow.highchart_code,
                        df_data: chartRow.df_data,
                        updated_df_data: chartRow.updated_df_data,
                        actions: chartRow.actions,
                        summary: chartRow.summary,
                        created_at: chartRow.executed_at,
                    };
                    setBaseChart(normalized);
                    setOverrideChart(normalized);
                }
            } catch (err) {
                console.error('Failed to load initial chart detail:', err);
                navigate('/dashboard');
            } finally {
                setIsLoading(false);
            }
        };
        loadInitialDetail();
    }, [qChartId, qDashboardId, location.search, navigate]);

    const chartData = useMemo(() => overrideChart, [overrideChart]);

    const dashboardName = useMemo(() => {
        if (!chartData) return 'Dashboard Chart';
        const dashId = qDashboardId ?? chartData?.dashboard_id;
        const dash = dashboards.find(d => String(d?.id) === String(dashId));
        return dash?.name || 'Dashboard Chart';
    }, [dashboards, chartData, qDashboardId]);

    const prepared = useMemo(() => {
        if (!chartData) return null;
        const tableData = getJSONObject(chartData?.updated_df_data ? chartData?.updated_df_data : chartData?.df_data);
        const dataToPlot = getJSONObject(chartData?.highchart_code);
        const created = new Date(chartData?.created_at);
        return {
            ...chartData,
            table: tableData,
            highchart_code: dataToPlot,
            createdAtDate: created.toLocaleDateString(),
            createdAtTime: `${created.getHours()}:${created.getMinutes()}`,
        };
    }, [chartData]);

    const applyDateFilter = async () => {
        if (!chartData) return;
        try {
            setIsApplying(true);
            const resp = await getDashboardChartFiltered({
                chartid: qChartId ?? chartData?.chartid,
                dashboard_id: qDashboardId ?? chartData?.dashboard_id,
                start_date: startDate || undefined,
                end_date: endDate || undefined,
            });
            // Expected shape: [ [ { ...fields } ], true/false ] but backend example shows similar array
            const rows = Array.isArray(resp?.[0]) ? resp[0] : [];
            const chartRow = rows?.[0] ?? null;
            if (chartRow) {
                const updated = {
                    ...chartData,
                    graph_type: chartRow.graph_type ?? chartData.graph_type ?? null,
                    graph_head: chartRow.graph_head ?? chartData.graph_head ?? null,
                    highchart_code: chartRow.highchart_code ?? chartData.highchart_code,
                    df_data: chartRow.df_data ?? chartData.df_data,
                    updated_df_data: chartRow.updated_df_data ?? chartData.updated_df_data,
                    actions: chartRow.actions ?? chartData.actions,
                    summary: chartRow.summary ?? chartData.summary,
                    created_at: chartRow.executed_at ?? chartData.created_at,
                };
                setOverrideChart(updated);
            }
        } catch (e) {
            console.error('Failed to fetch filtered chart detail:', e);
        } finally {
            setIsApplying(false);
        }
    };

    const clearFilters = () => {
        setStartDate("");
        setEndDate("");
        if (baseChart) {
            setOverrideChart(baseChart);
        }
    };

    if (isLoading || !prepared) {
        return (
            <Layout>
                <div className="p-10 text-white">
                    <div>Loading chart...</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-10 text-white">
                <div className="flex flex-col gap-9">
                    {isFilterable && (
                    <div className="flex items-end gap-4">
                        <div className="flex flex-col">
                            <label className="text-xs text-[#DADCDE] mb-1">Start date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className={`bg-primary text-white border border-[#232B3A] rounded px-3 py-2 text-sm`}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-xs text-[#DADCDE] mb-1">End date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className={`bg-primary text-white border border-[#232B3A] rounded px-3 py-2 text-sm`}
                            />
                        </div>
                        <button
                            disabled={isApplying}
                            onClick={applyDateFilter}
                            className={`px-4 py-2 rounded bg-[#7357eb] hover:bg-[#6246db] disabled:opacity-50`}
                        >
                            {isApplying ? 'Applying...' : 'Apply'}
                        </button>
                        <button
                            disabled={isApplying}
                            onClick={clearFilters}
                            className={`px-4 py-2 rounded border border-[#232B3A] hover:bg-[#1E293B]`}
                        >
                            Clear
                        </button>
                    </div>
                    )}

                    <div className="flex flex-col gap-2 p-4 border border-[#BEEF9E66] rounded-lg">
                        <div className={`flex flex-col gap-2`}>
                            <h2 className="font-medium text-sm">{dashboardName}</h2>
                            <p className="text-xs text-[#DADCDE]">Created on {prepared?.createdAtDate} at {prepared?.createdAtTime}</p>
                        </div>

                        <hr className="border-[#BEEF9E66]" />

                        <HighchartsComponentAlt
                            chartTypeOuter={prepared?.graph_type}
                            chartTitle={prepared?.graph_head}
                            data={prepared?.table}
                            dataToPlot={prepared?.highchart_code}
                        />
                    </div>

                    {prepared?.actions && (
                        <div>
                            <h3 className="font-medium text-base text-left">Recommended actions:</h3>
                            <div className={`mt-5 overflow-scroll text-xs p-5 bg-primary text-left ${styles.recommended_actions}`}>
                                <div className="text-left" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(prepared?.actions) }} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export default DashboardDetailWindow;


